import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, AuthState, LoginCredentials, SignUpCredentials } from '../types';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<boolean>;
  signUp: (credentials: SignUpCredentials) => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Mock user data for demonstration
const MOCK_USERS: User[] = [
  {
    id: '1',
    username: 'student',
    email: 'student@university.edu',
    role: 'student',
    name: 'Student User',
    avatar: 'S',
    studentId: 'STU001',
    mobileNumber: '1234567890',
    department: 'Computer Science'
  },
  {
    id: '2',
    username: 'john.doe',
    email: 'john.doe@university.edu',
    role: 'student',
    name: 'John Doe',
    avatar: 'J',
    studentId: 'STU002',
    mobileNumber: '9876543210',
    department: 'Engineering'
  }
];

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null
  });

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('auth-token');
        const userData = localStorage.getItem('user-data');
        
        if (token && userData) {
          const user = JSON.parse(userData);
          setAuthState({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null
          });
        } else {
          setAuthState(prev => ({
            ...prev,
            isLoading: false
          }));
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: 'Session expired. Please login again.'
        });
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Find user in mock data
      const user = MOCK_USERS.find(u => 
        u.username === credentials.username && 
        credentials.password === 'password' // Simple password for demo
      );

      if (user) {
        const token = `token_${user.id}_${Date.now()}`;
        
        // Store in localStorage
        localStorage.setItem('auth-token', token);
        localStorage.setItem('user-data', JSON.stringify(user));

        setAuthState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null
        });

        return true;
      } else {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Invalid username or password'
        }));
        return false;
      }
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Login failed. Please try again.'
      }));
      return false;
    }
  };

  const signUp = async (credentials: SignUpCredentials): Promise<boolean> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Check if username already exists
      const existingUser = MOCK_USERS.find(u => u.username === credentials.username);
      if (existingUser) {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: 'Username already exists'
        }));
        return false;
      }

      // Create new user
      const newUser: User = {
        id: String(MOCK_USERS.length + 1),
        username: credentials.username,
        email: credentials.email,
        role: 'student',
        name: credentials.name,
        avatar: credentials.name.charAt(0).toUpperCase(),
        studentId: `STU${String(MOCK_USERS.length + 1).padStart(3, '0')}`,
        mobileNumber: credentials.mobileNumber,
        department: 'General'
      };

      // In a real app, this would be an API call
      MOCK_USERS.push(newUser);

      const token = `token_${newUser.id}_${Date.now()}`;
      
      // Store in localStorage
      localStorage.setItem('auth-token', token);
      localStorage.setItem('user-data', JSON.stringify(newUser));

      setAuthState({
        user: newUser,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });

      return true;
    } catch (error) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Sign up failed. Please try again.'
      }));
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('auth-token');
    localStorage.removeItem('user-data');
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null
    });
  };

  const clearError = () => {
    setAuthState(prev => ({ ...prev, error: null }));
  };

  const value: AuthContextType = {
    ...authState,
    login,
    signUp,
    logout,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

